import { Inject, Injectable } from '@angular/core';
import { FirebaseApp } from 'firebase/app';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { CommonFireStoreDao } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { FIREBASE_APP } from 'ag-common-svc/lib/injections/firebase-app';

export interface FirestoreSignalWatchArgs<TSignal, TValue> {
  collectionPath: string;
  documentId: string;
  mapData: (signal: TSignal | undefined) => TValue;
  distinctComparator?: (previous: TValue, current: TValue) => boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FirestoreSignalsService {
  private readonly fsDao: CommonFireStoreDao<any>;

  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    this.fsDao = new CommonFireStoreDao<any>(fireBaseApp, null, null);
  }

  watchSignal<TSignal, TValue>(args: FirestoreSignalWatchArgs<TSignal, TValue>): Observable<TValue> {
    return this.fsDao.getDocument(args.collectionPath, args.documentId).pipe(
      map(snapshot => {
        const signalData = snapshot?.data() as TSignal | undefined;
        return args.mapData(signalData);
      }),
      distinctUntilChanged(args.distinctComparator),
    );
  }

  watchSignalVersion<TSignal>(
    args: Omit<FirestoreSignalWatchArgs<TSignal, number>, 'mapData'> & {
      mapData: (signal: TSignal | undefined) => number | null | undefined;
      fallbackVersion?: number;
    },
  ): Observable<number> {
    const fallbackVersion = Number(args.fallbackVersion ?? 0);

    return this.watchSignal<TSignal, number>({
      ...args,
      mapData: signal => {
        const version = Number(args.mapData(signal));
        return Number.isFinite(version) ? version : fallbackVersion;
      },
    });
  }
}
